import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { debugLog, trackFocusChange } from '../utils/debug';

interface UncontrolledInputProps {
  id: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  autoComplete?: string;
  className?: string;
  debounceMs?: number;
}

export interface UncontrolledInputRef {
  focus: () => void;
  blur: () => void;
  getValue: () => string;
}

const UncontrolledInput = forwardRef<UncontrolledInputRef, UncontrolledInputProps>(({
  id,
  type = 'text',
  value,
  onChange,
  placeholder,
  style,
  autoComplete = 'off',
  className,
  debounceMs = 150
}, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const lastReportedValue = useRef('');
  const ignoreNextChange = useRef(false);

  useImperativeHandle(ref, () => ({
    focus: () => {
      debugLog('UncontrolledInput', `Focus called via ref`, { id });
      inputRef.current?.focus();
    },
    blur: () => {
      debugLog('UncontrolledInput', `Blur called via ref`, { id });
      inputRef.current?.blur();
    },
    getValue: () => inputRef.current?.value || ''
  }));

  // Update DOM value when prop changes (but only if it's different from current DOM value)
  useEffect(() => {
    if (inputRef.current && inputRef.current.value !== value) {
      debugLog('UncontrolledInput', `Syncing DOM value`, { 
        id, 
        domValue: inputRef.current.value, 
        propValue: value,
        ignoreNext: ignoreNextChange.current
      });
      
      if (ignoreNextChange.current) {
        // This is our own change coming back, don't update DOM
        ignoreNextChange.current = false;
        debugLog('UncontrolledInput', `Ignoring own change reflection`, { id });
      } else {
        // Preserve cursor position
        const selectionStart = inputRef.current.selectionStart;
        const selectionEnd = inputRef.current.selectionEnd;
        
        inputRef.current.value = value;
        
        // Restore cursor position
        if (selectionStart !== null && selectionEnd !== null) {
          inputRef.current.setSelectionRange(selectionStart, selectionEnd);
        }
        
        debugLog('UncontrolledInput', `DOM value updated`, { id, newValue: value });
      }
    }
  }, [value, id]);

  const reportChange = (newValue: string) => {
    if (newValue !== lastReportedValue.current) {
      debugLog('UncontrolledInput', `Reporting change to parent`, { id, value: newValue });
      trackFocusChange('UncontrolledInput', `Before parent onChange for ${id}`);
      
      ignoreNextChange.current = true;
      lastReportedValue.current = newValue;
      onChange(newValue);
      
      setTimeout(() => {
        trackFocusChange('UncontrolledInput', `After parent onChange for ${id}`);
      }, 0);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    debugLog('UncontrolledInput', `Input change event`, { id, newValue });
    
    // Clear existing debounce
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    // Set up new debounce
    debounceTimeout.current = setTimeout(() => {
      reportChange(newValue);
    }, debounceMs);
  };

  const handleBlur = () => {
    debugLog('UncontrolledInput', `Blur event - immediate report`, { id });
    
    // Clear debounce and report immediately on blur
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    if (inputRef.current) {
      reportChange(inputRef.current.value);
    }
  };

  const handleFocus = () => {
    debugLog('UncontrolledInput', `Focus event`, { id });
    trackFocusChange('UncontrolledInput', `Focus gained on ${id}`);
  };

  // Cleanup on unmount
  useEffect(() => {
    debugLog('UncontrolledInput', `Component mounted`, { id, initialValue: value });
    
    // Set initial DOM value
    if (inputRef.current) {
      inputRef.current.value = value;
    }
    
    return () => {
      debugLog('UncontrolledInput', `Component unmounted`, { id });
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [id, value]);

  return (
    <input
      ref={inputRef}
      id={id}
      type={type}
      defaultValue={value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      style={style}
      autoComplete={autoComplete}
      data-lpignore="true"
      data-1p-ignore="true" 
      data-bwignore="true"
      className={className}
    />
  );
});

UncontrolledInput.displayName = 'UncontrolledInput';

export default UncontrolledInput;